import { useState } from "react";
import { Mail, MessageCircle, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import SEO from "../components/SEO";
import { Reveal } from "../components/Reveal";
import Breadcrumbs from "../components/Breadcrumbs";
import { Field, inputCls } from "../components/ToolShell";
import { api } from "../lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error("Please fill all fields.");
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Thank you! We'll be in touch soon 🙏");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Please enter a valid email and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-28 md:pt-32">
      <SEO title="Contact Us" description="Get in touch with the Numoraa team. We'd love to hear your questions, feedback and stories." path="/contact" />
      <div className="max-w-5xl mx-auto px-5 md:px-8 pb-24">
        <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]} />
        <div className="mt-6 grid gap-12 md:grid-cols-2">
          <Reveal>
            <h1 className="font-serif text-5xl md:text-6xl font-light tracking-tighter">Say <span className="italic text-primary">hello</span>.</h1>
            <p className="mt-6 text-lg font-light text-muted-foreground leading-relaxed">Questions, feedback or just want to share your reading? We read every message.</p>
            <div className="mt-8 space-y-4">
              <div className="flex items-center gap-3 text-sm">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-secondary"><Mail className="h-5 w-5 text-primary" /></span>
                <a href="mailto:hello@numoraa.com" className="text-muted-foreground hover:text-primary">hello@numoraa.com</a>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-secondary"><MessageCircle className="h-5 w-5 text-primary" /></span>
                <span className="text-muted-foreground">Or chat live with an AI Pandit anytime</span>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <form onSubmit={submit} className="rounded-3xl border border-border bg-card p-7 shadow-soft space-y-6" data-testid="contact-form">
              <Field label="Your Name"><input className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="contact-name" /></Field>
              <Field label="Email"><input type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-email" /></Field>
              <Field label="Message"><textarea rows={4} className={`${inputCls} resize-none`} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="contact-message" /></Field>
              <button disabled={loading} data-testid="contact-submit" className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-primary-foreground transition-all hover:-translate-y-0.5 hover:shadow-gold disabled:opacity-60">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 text-gold" />} Send message
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </div>
  );
}